import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import pickBy from 'lodash/pickBy';
import isNil from 'lodash/isNil';
import { useRouteMatch, useHistory } from 'react-router-dom';
import { useMount, usePrevious } from 'react-use';
import isEmpty from 'lodash/isEmpty';

import { Logger } from '../../../../../common/infrastructure/logger';
import {
  CustomError,
  useAppErrors,
} from '../../../../../common/contexts/app-error-context';
import Responsive, {
  useMobile,
} from '../../../../../common/components/responsive/Responsive';
import {
  ADD_FEATURE_TYPE,
  compilePath,
  FEEDBACK_WIZARD_STEP,
} from '../../../../../common/utils/helpers/routes';
import {
  formStateChanged,
  selectFormState,
  clearFormState,
  selectMarkerPositionCountryCode,
  clearRoutablePoints,
  selectCurrentFormId,
  currentFormIdChanged,
} from '../../../../../common/store/item-edit';
import { getLastSubmittedEmail } from '../../../../../common/utils/helpers/emailCache';
import {
  initialLoadingPhotosHandler,
  newPhotosHandler,
  photosReducer,
} from '../../../../../common/components/controls/photo-upload/PhotoUpload';
import Button, {
  appearances,
} from '../../../../../common/components/controls/button/Button';
import {
  EDIT_SUGGESTION_TYPES,
  EDIT_SUGGESTION_VERSION,
} from '../../../../../common/utils/constants/constants';
import { selectUserNotificationEnabled } from '../../../../../common/store/user-notificaition-settings';
import { ClearanceType } from '../../../../../common/components/clearance/Clearance';
import Form, {
  FormField,
} from '../../../../../common/components/forms/form/Form';
import DefaultFormFooterHint from '../../../../../common/components/forms/default-form-footer-hint/DefaultFormFooterHint';
import { FIELD_TYPES } from '../../../../../common/components/forms/field/Field';
import { validators } from '../../../../../common/utils/helpers/validators';
import {
  sendEditSuggestionAction,
  storeLastSubmittedEmailAction,
  analyticsTrackAction,
} from '../../../../../common/utils/helpers/EditSuggestionActions';

import './AddClearanceForm.scss';
import {
  getReferrer,
  getReferrerAccount,
} from '../../../../../common/utils/helpers/editSuggestions';

const CLEARANCE_TYPE_OPTIONS = {
  [ClearanceType.weight]: {
    value: 'weight',
    label: 'Weight',
  },
  [ClearanceType.height]: {
    value: 'height',
    label: 'Height',
  },
  [ClearanceType.length]: {
    value: 'length',
    label: 'Length',
  },
  [ClearanceType.width]: {
    value: 'width',
    label: 'Width',
  },
};

const CLEARANCE_CLASS_OPTIONS = {
  railwayOverpass: {
    value: 'railwayOverpass',
    label: 'Railway Overpass',
  },
  highwayOverpass: {
    value: 'highwayOverpass',
    label: 'Highway Overpass',
  },
  bridgeCeiling: {
    value: 'bridgeCeiling',
    label: 'Bridge Ceiling',
  },
  drawBridge: {
    value: 'drawBridge',
    label: 'Draw Bridge',
  },
  tunnel: {
    value: 'tunnel',
    label: 'Tunnel',
  },
  pedestrianOverpass: {
    value: 'pedestrianOverpass',
    label: 'Pedestrian Overpass',
  },
  other: {
    value: 'other',
    label: 'Other',
  },
};

const getAdditionalInformationFormFields = ({
  attachmentsDispatch,
}): FormField[] => [
  {
    field: 'email',
    label: 'Your email',
    defaultValue: '',
    getPrefillValue: () => getLastSubmittedEmail(),
    required: true,
    validate: validators.compose(
      validators.required('Email'),
      validators.email,
    ),
    type: FIELD_TYPES.input,
    controlProps: {
      placeholder: 'You will get an email when changes are made',
      // name and autocomplete are required for email autocompletion done by browser
      name: 'email',
      autoComplete: 'email',
    },
  },
  {
    field: 'comment',
    label: 'Comments',
    defaultValue: '',
    type: FIELD_TYPES.textArea,
    controlProps: {
      placeholder: 'Add here some additional information',
    },
  },
  {
    field: 'attachments',
    label: 'Photos',
    defaultValue: {},
    type: FIELD_TYPES.photos,
    controlProps: { photosDispatch: attachmentsDispatch },
  },
];

const getFeedbackFormFields = ({
  shouldShowOtherClearanceClassInput,
  clearanceType,
}: {
  shouldShowOtherClearanceClassInput;
  clearanceType;
}): FormField[] => {
  const fields: FormField[] = [
    {
      field: 'clearanceType',
      label: 'Clearance Type',
      required: true,
      validate: validators.required('Clearance Type'),
      defaultValue: '',
      type: FIELD_TYPES.select,
      controlProps: {
        options: [
          CLEARANCE_TYPE_OPTIONS.weight,
          CLEARANCE_TYPE_OPTIONS.height,
          CLEARANCE_TYPE_OPTIONS.length,
          CLEARANCE_TYPE_OPTIONS.width,
        ],
        notSelectedOption: { label: 'Choose type of Clearance', value: '' },
      },
    },
  ];

  if (clearanceType) {
    fields.push({
      field: 'clearanceValue',
      label: 'Clearance Value',
      required: true,
      validate: validators.validateSelected(
        validators.required('Clearance Value'),
        (clearanceValue) =>
          clearanceValue && Object.values(clearanceValue).join(''),
      ),
      defaultValue: '',
      type: FIELD_TYPES.clearance,
      controlProps: {
        type: clearanceType,
      },
    });
  }

  fields.push({
    field: 'clearanceClass',
    label: 'Clearance Class',
    required: true,
    validate: validators.required('Clearance Class'),
    defaultValue: '',
    type: FIELD_TYPES.select,
    controlProps: {
      options: [
        CLEARANCE_CLASS_OPTIONS.railwayOverpass,
        CLEARANCE_CLASS_OPTIONS.highwayOverpass,
        CLEARANCE_CLASS_OPTIONS.bridgeCeiling,
        CLEARANCE_CLASS_OPTIONS.drawBridge,
        CLEARANCE_CLASS_OPTIONS.tunnel,
        CLEARANCE_CLASS_OPTIONS.pedestrianOverpass,
        CLEARANCE_CLASS_OPTIONS.other,
      ],
      notSelectedOption: { label: 'Choose class of Clearance', value: '' },
    },
  });

  if (shouldShowOtherClearanceClassInput) {
    fields.push({
      field: 'clearanceOtherClass',
      label: 'Clearance Other Class',
      required: true,
      validate: validators.compose(
        validators.required('Clearance Other Class'),
        validators.maxLength(50, 'Clearance Other Class'),
      ),
      defaultValue: '',
      type: FIELD_TYPES.input,
    });
  }

  fields.push(
    {
      field: 'multiLaneClearance',
      label: 'Is this a Multi-Lane Clearance',
      defaultValue: false,
      type: FIELD_TYPES.switch,
      checkChanged: () => true,
    },
    {
      field: 'verificationLink',
      label: 'Verification Link',
      defaultValue: '',
      type: FIELD_TYPES.input,
      controlProps: {
        title: 'Add a verification link URL',
        placeholder: 'Add a verification link URL',
      },
    },
  );

  return fields;
};

const getJointFormFields = ({
  attachmentsDispatch,
  shouldShowOtherClearanceClassInput,
  clearanceType,
}: {
  attachmentsDispatch;
  shouldShowOtherClearanceClassInput;
  clearanceType?;
}) => [
  ...getFeedbackFormFields({
    shouldShowOtherClearanceClassInput,
    clearanceType,
  }),
  ...getAdditionalInformationFormFields({ attachmentsDispatch }),
];

const FORM_ID = 'AddClearanceForm';

export interface AddClearanceFormProps {
  userLocation;
  itemDragPosition;
  formDisabled;
  onSubmit;
  onCancel;
}

interface RouteMatch {
  wizardStep;
}

const AddClearanceForm = ({
  userLocation,
  itemDragPosition,
  formDisabled,
  onSubmit,
  onCancel,
}: AddClearanceFormProps) => {
  const { showErrorPage } = useAppErrors();
  const history = useHistory();
  const match = useRouteMatch<RouteMatch>();
  const isMobile = useMobile();
  const [isLoading, setLoadingState] = useState(false);

  const notificationsEnabled = useSelector(selectUserNotificationEnabled);
  const formState = useSelector(selectFormState);
  const currentFormId = useSelector(selectCurrentFormId);
  const formStateRef = useRef<Record<string, unknown>>();
  formStateRef.current = formState;

  const dispatch = useDispatch();
  const setFormState = useCallback(
    (formState) => dispatch(formStateChanged(formState)),
    [dispatch],
  );

  // clean up other forms to keep local storage size as small as possible
  useMount(() => {
    if (currentFormId !== FORM_ID) {
      dispatch(clearFormState());
      dispatch(currentFormIdChanged(FORM_ID));
    }

    dispatch(clearRoutablePoints());
  });

  const [isFeedbackFormShown, setIsFeedbackFormShown] = useState(!!formState);
  // guarantee that feedback is always filled with all the data on mobile
  useEffect(() => {
    if (isFeedbackFormShown) {
      if (isMobile && !match.params.wizardStep) {
        history.replace({
          pathname: compilePath('add', null, {
            featureType: ADD_FEATURE_TYPE.clearance,
            wizardStep: FEEDBACK_WIZARD_STEP.feedback,
          }),
          search: history.location.search,
        });
      }

      return;
    }

    if (!isMobile) {
      setIsFeedbackFormShown(true);

      return;
    }

    if (
      !match.params.wizardStep ||
      match.params.wizardStep === FEEDBACK_WIZARD_STEP.additionalInformation
    ) {
      history.replace({
        pathname: compilePath('add', null, {
          featureType: ADD_FEATURE_TYPE.clearance,
          wizardStep: FEEDBACK_WIZARD_STEP.location,
        }),
        search: history.location.search,
      });
    } else if (match.params.wizardStep === FEEDBACK_WIZARD_STEP.feedback) {
      setIsFeedbackFormShown(true);
    }
  }, [isFeedbackFormShown, isMobile, match, history]);

  const attachmentsDispatch = useCallback(
    (action) => {
      if (!formStateRef.current) {
        return;
      }

      const newAttachments = photosReducer(
        (formStateRef.current.values as Record<string, any>).attachments,
        action,
      );
      const newFormState = {
        ...formStateRef.current,
        values: {
          ...(formStateRef.current.values as Record<string, any>),
          attachments: newAttachments,
        },
      };
      // set ref to handle sequential actions between state updates and rerenders
      formStateRef.current = newFormState;

      setFormState(newFormState);

      return action;
    },
    [setFormState],
  );

  const attachments = useMemo(() => formState?.values.attachments, [formState]);

  // reload all still loading images, because it won't be handled anyway
  useMount(() => {
    if (attachments) {
      initialLoadingPhotosHandler(attachments, attachmentsDispatch);
    }
  });

  useEffect(() => {
    if (attachments) {
      newPhotosHandler(attachments, attachmentsDispatch);
    }
  }, [attachments, attachmentsDispatch]);

  const clearanceType = useMemo(
    () => formState?.values?.clearanceType,
    [formState],
  );

  const shouldShowOtherClearanceClassInput = useMemo(
    () =>
      formState?.values?.clearanceClass === CLEARANCE_CLASS_OPTIONS.other.value,
    [formState],
  );

  const jointFormFields = useMemo(
    () =>
      getJointFormFields({
        attachmentsDispatch,
        shouldShowOtherClearanceClassInput,
        clearanceType,
      }),
    [attachmentsDispatch, shouldShowOtherClearanceClassInput, clearanceType],
  );
  const feedbackFormFields = useMemo(
    () =>
      getFeedbackFormFields({
        shouldShowOtherClearanceClassInput,
        clearanceType,
      }),
    [shouldShowOtherClearanceClassInput, clearanceType],
  );
  const additionalInformationFormFields = useMemo(
    () =>
      getAdditionalInformationFormFields({
        attachmentsDispatch,
      }),
    [attachmentsDispatch],
  );

  // it's required to have all the fields in place so initial value is filled correctly
  const fullFormFields = useMemo(
    () =>
      getJointFormFields({
        attachmentsDispatch,
        shouldShowOtherClearanceClassInput: true,
      }),
    [attachmentsDispatch],
  );
  const initialValues = useMemo(
    () =>
      fullFormFields.reduce((acc, { field, defaultValue }) => {
        acc[field] = defaultValue;
        return acc;
      }, {}),
    [fullFormFields],
  );

  useEffect(() => {
    if (!formState) {
      const values = jointFormFields.reduce(
        (acc, { field, defaultValue, getPrefillValue }) => {
          acc[field] = (getPrefillValue && getPrefillValue()) || defaultValue;
          return acc;
        },
        {},
      );

      setFormState({ values });
    }
  }, [formState, setFormState, jointFormFields]);

  const prevClearanceType = usePrevious(clearanceType);
  useEffect(() => {
    if (prevClearanceType && prevClearanceType !== clearanceType) {
      if (!formState) {
        return;
      }

      const newFormState = {
        ...formState,
        values: {
          ...formState.values,
          clearanceValue: {},
        },
      };
      // set ref to handle sequential actions between state updates and rerenders
      formStateRef.current = newFormState;

      setFormState(newFormState);
    }
  }, [clearanceType, formState, prevClearanceType, setFormState]);

  const handleSubmit = useCallback(
    async (values) => {
      const {
        email,
        comment,
        attachments,
        /* eslint-disable @typescript-eslint/no-unused-vars */
        name,
        oneWayRoad,
        speedLimit,
        /* eslint-enable @typescript-eslint/no-unused-vars */
        ...changes
      } = values;

      const clearanceValues: string[] = [];
      if (changes.clearanceType === ClearanceType.weight) {
        clearanceValues.push(`${changes.clearanceValue.tons}T`);
      } else {
        clearanceValues.push(`${changes.clearanceValue.feet || 0}'`);

        if (!isNil(changes.clearanceValue.inches)) {
          clearanceValues.push(`${changes.clearanceValue.inches}"`);
        }
      }

      const payload = pickBy(
        {
          version: EDIT_SUGGESTION_VERSION,
          action: 'create',
          reason: 'missing_clearance',
          location: itemDragPosition && {
            longitude: itemDragPosition[0],
            latitude: itemDragPosition[1],
          },
          userEmail: email,
          userLocation: !isEmpty(userLocation)
            ? {
                longitude: userLocation.longitude,
                latitude: userLocation.latitude,
              }
            : null,
          referrer: getReferrer(),
          referrerAccount: getReferrerAccount(),
          notificationsDisabled: !notificationsEnabled,
          comment,
          attachments: Object.values(attachments as any[])
            .filter(({ fileId }) => fileId)
            .map(({ fileId }) => fileId), //
          changes: pickBy(
            {
              type: changes.clearanceType,
              value: clearanceValues.join(' '),
              class: changes.clearanceClass,
              otherClass:
                changes.clearanceClass === CLEARANCE_CLASS_OPTIONS.other.value
                  ? changes.clearanceOtherClass
                  : null,
              isMultiLane: changes.multiLaneClearance,
              authoritativeSources: changes.verificationLink,
            },
            (val) => !isNil(val) && val !== '',
          ),
        },
        (val) => !isNil(val) && val !== '',
      );

      try {
        setLoadingState(true);

        const editSuggestionResult = await sendEditSuggestionAction(
          {
            editSuggestionType: EDIT_SUGGESTION_TYPES.clearance,
            origin: FORM_ID,
          },
          payload,
        );

        storeLastSubmittedEmailAction(
          {
            email,
            origin: FORM_ID,
          },
          editSuggestionResult,
        );

        analyticsTrackAction({
          analyticsTrackingMessage: 'Clearance Addition Submit',
          origin: FORM_ID,
          analyticsTrackParams: {
            coordinates: itemDragPosition,
          },
        });

        if (onSubmit) {
          onSubmit(editSuggestionResult.result);
        }
      } catch (error) {
        const _error: CustomError = error as CustomError;

        Logger.error('Error while submitting edit suggestion', _error.message);

        showErrorPage({
          type: _error.type,
          message: _error.message,
        } as CustomError);
      } finally {
        setLoadingState(false);
      }
    },
    [
      itemDragPosition,
      notificationsEnabled,
      onSubmit,
      showErrorPage,
      userLocation,
    ],
  );

  const handleLocationFormSubmit = useCallback(
    () =>
      history.push({
        pathname: compilePath('add', null, {
          featureType: ADD_FEATURE_TYPE.clearance,
          wizardStep: FEEDBACK_WIZARD_STEP.feedback,
        }),
        search: history.location.search,
      }),
    [history],
  );

  const handleFeedbackFormSubmit = useCallback(
    () =>
      history.push({
        pathname: compilePath('add', null, {
          featureType: ADD_FEATURE_TYPE.clearance,
          wizardStep: FEEDBACK_WIZARD_STEP.additionalInformation,
        }),
        search: history.location.search,
      }),
    [history],
  );

  const { wizardStep } = match.params;

  const markerPositionCountryCode = useSelector(
    selectMarkerPositionCountryCode,
  );

  const outerInput = useMemo(
    () => ({ markerPositionCountryCode }),
    [markerPositionCountryCode],
  );

  return (
    <>
      <Responsive mobile={false}>
        <Form
          className="add-clearance-form"
          state={formState}
          fields={jointFormFields}
          initialValues={initialValues}
          outerInput={outerInput}
          formDisabled={formDisabled || isLoading}
          onStateChange={setFormState}
          onSubmit={handleSubmit}
          onCancel={onCancel}
          footerHint={<DefaultFormFooterHint />}
        />
      </Responsive>
      <Responsive mobile={true}>
        {wizardStep === FEEDBACK_WIZARD_STEP.location && (
          <div className="add-clearance-form__location-pickup-button-container">
            <Button
              appearance={appearances.PRIMARY}
              disabled={formDisabled}
              onClick={handleLocationFormSubmit}
            >
              Next
            </Button>
          </div>
        )}
        {wizardStep === FEEDBACK_WIZARD_STEP.feedback && (
          <Form
            className="add-clearance-form"
            state={formState}
            fields={feedbackFormFields}
            initialValues={initialValues}
            submitLabel="Next"
            outerInput={outerInput}
            formDisabled={formDisabled}
            stepLabel="1/2"
            onStateChange={setFormState}
            onSubmit={handleFeedbackFormSubmit}
          />
        )}

        {wizardStep === FEEDBACK_WIZARD_STEP.additionalInformation && (
          <Form
            state={formState}
            fields={additionalInformationFormFields}
            initialValues={initialValues}
            formDisabled={isLoading}
            stepLabel="2/2"
            onStateChange={setFormState}
            onSubmit={handleSubmit}
            footerHint={<DefaultFormFooterHint />}
          />
        )}
      </Responsive>
    </>
  );
};

export default AddClearanceForm;
